/*
 * Map a mouse click on a rendered page canvas to PDF user-space coordinates.
 * PDF coords have origin at the bottom-left and Y growing upward; DOM/canvas
 * coords have origin at the top-left and Y growing downward — so the Y axis
 * gets flipped. We scale by (PDF size / canvas CSS size), not device pixels,
 * because the click event reports CSS pixels.
 *
 * The caller is responsible for ensuring rotation === 0; rotated layouts need
 * the inverse rotation applied first. (We gate the click handler in the
 * viewer rather than handling all four rotations here to keep this small.)
 */

export interface PageBox {
  /** PDF page width in points. */
  readonly widthPt: number;
  /** PDF page height in points. */
  readonly heightPt: number;
  /** Canvas bounding rect in CSS pixels. */
  readonly rect: { readonly width: number; readonly height: number };
}

export interface PdfPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * `clientX/Y` from a MouseEvent or pointer, plus the canvas's getBoundingClientRect
 * (we pass rect.left/top in `relativeTo`). Returns PDF-space (x, y in points).
 */
export function clickToPdfPoint(
  clientX: number,
  clientY: number,
  relativeTo: { left: number; top: number },
  page: PageBox,
): PdfPoint {
  const cssX = clientX - relativeTo.left;
  const cssY = clientY - relativeTo.top;
  const sx = page.widthPt / page.rect.width;
  const sy = page.heightPt / page.rect.height;
  return {
    x: clamp(cssX * sx, 0, page.widthPt),
    // Y flip: PDF origin is bottom-left, so a click near the top maps near pageHeight.
    y: clamp(page.heightPt - cssY * sy, 0, page.heightPt),
  };
}

/**
 * Inverse mapping for overlay rendering: given a PDF-coord rect, return CSS
 * pixel coords inside the canvas. Used to draw pending edit overlays on top
 * of the rendered page.
 */
export function pdfRectToCssRect(
  rect: { x: number; y: number; width: number; height: number },
  page: PageBox,
): { left: number; top: number; width: number; height: number } {
  const sx = page.rect.width / page.widthPt;
  const sy = page.rect.height / page.heightPt;
  return {
    left: rect.x * sx,
    // Y flip the *top* edge: PDF (x, y) is the rect's bottom-left, so the CSS
    // top = canvas height - (y + height) in PDF terms, then scaled.
    top: (page.heightPt - (rect.y + rect.height)) * sy,
    width: rect.width * sx,
    height: rect.height * sy,
  };
}

export type RotationDelta = 0 | 90 | 180 | 270;

/**
 * Rotation-aware overlay mapping. Edits store PDF coords in the page's
 * *original* display frame (overlays are only ever placed at the original
 * rotation — placement is gated on that). When the user then rotates the page,
 * pdf.js re-renders the canvas rotated clockwise by `delta`, swapping the
 * canvas dims for 90/270. This maps the stored rect into that rotated canvas
 * so the overlay follows the page.
 *
 * `box` is the *current* (already-rotated) canvas CSS size. We first undo the
 * swap to recover the original-frame CSS rect, then rotate it clockwise by
 * `delta` into the rotated box. Clockwise matches pdf.js getViewport rotation.
 */
export function pdfRectToCssRectRotated(
  rect: { x: number; y: number; width: number; height: number },
  page: { widthPt: number; heightPt: number },
  box: { width: number; height: number },
  delta: RotationDelta,
): { left: number; top: number; width: number; height: number } {
  // Recover the unrotated canvas dims (90/270 swap width/height).
  const u = delta === 90 || delta === 270
    ? { width: box.height, height: box.width }
    : { width: box.width, height: box.height };

  // Unrotated CSS rect (top-left origin, Y already flipped).
  const sx = u.width / page.widthPt;
  const sy = u.height / page.heightPt;
  const left = rect.x * sx;
  const top = (page.heightPt - (rect.y + rect.height)) * sy;
  const width = rect.width * sx;
  const height = rect.height * sy;

  // Rotate the axis-aligned rect clockwise within the unrotated box (u),
  // producing coords in the rotated box. Derived by mapping all four corners.
  switch (delta) {
    case 90:
      return { left: u.height - (top + height), top: left, width: height, height: width };
    case 180:
      return { left: u.width - (left + width), top: u.height - (top + height), width, height };
    case 270:
      return { left: top, top: u.width - (left + width), width: height, height: width };
    default:
      return { left, top, width, height };
  }
}

/**
 * Inverse of {@link pdfRectToCssRectRotated} for a single pointer position:
 * map a CSS pixel point inside the ROTATED canvas back to a PDF point in the
 * page's ORIGINAL (unrotated) frame — the frame every stored edit lives in.
 * This is what placement/move gestures need so they work on rotated pages
 * instead of being gated to delta === 0.
 *
 * `box` is the CURRENT (already-rotated) canvas CSS size (90/270 have swapped
 * dims). We undo the rotation into the unrotated box `u`, then flip Y.
 */
export function clickToPdfPointRotated(
  clientX: number,
  clientY: number,
  relativeTo: { left: number; top: number },
  page: PageBox,
  delta: RotationDelta,
): PdfPoint {
  const cx = clientX - relativeTo.left;
  const cy = clientY - relativeTo.top;
  const u =
    delta === 90 || delta === 270
      ? { width: page.rect.height, height: page.rect.width }
      : { width: page.rect.width, height: page.rect.height };

  // Undo the clockwise canvas rotation: (cx, cy) in the rotated box → (ux, uy)
  // in the unrotated box. Derived by inverting the corner map of
  // pdfRectToCssRectRotated for a point.
  let ux: number;
  let uy: number;
  switch (delta) {
    case 90:
      ux = cy;
      uy = u.height - cx;
      break;
    case 180:
      ux = u.width - cx;
      uy = u.height - cy;
      break;
    case 270:
      ux = u.width - cy;
      uy = cx;
      break;
    default:
      ux = cx;
      uy = cy;
      break;
  }

  const px = (ux * page.widthPt) / u.width;
  // Y flip: top-left CSS origin → bottom-left PDF origin.
  const py = page.heightPt - (uy * page.heightPt) / u.height;
  return { x: clamp(px, 0, page.widthPt), y: clamp(py, 0, page.heightPt) };
}

/**
 * Inverse of {@link pdfRectToCssRectRotated} for a whole rect: given a CSS rect
 * in the ROTATED canvas, recover the PDF rect (original frame). This is what
 * drag-to-place and ↘-resize need on rotated pages — they build a CSS rect on
 * screen and convert it back to a PDF rect in one step, so the committed
 * geometry re-projects to exactly the box the user drew. Round-trips with
 * pdfRectToCssRectRotated for all four rotations (see tests).
 */
export function cssRectToPdfRectRotated(
  cssRect: { left: number; top: number; width: number; height: number },
  page: { widthPt: number; heightPt: number },
  box: { width: number; height: number },
  delta: RotationDelta,
): { x: number; y: number; width: number; height: number } {
  const u =
    delta === 90 || delta === 270
      ? { width: box.height, height: box.width }
      : { width: box.width, height: box.height };

  // Undo the clockwise rotation → the unrotated CSS rect (top-left origin).
  let left0: number;
  let top0: number;
  let width0: number;
  let height0: number;
  switch (delta) {
    case 90:
      width0 = cssRect.height;
      height0 = cssRect.width;
      left0 = cssRect.top;
      top0 = u.height - (cssRect.left + height0);
      break;
    case 180:
      width0 = cssRect.width;
      height0 = cssRect.height;
      left0 = u.width - (cssRect.left + width0);
      top0 = u.height - (cssRect.top + height0);
      break;
    case 270:
      width0 = cssRect.height;
      height0 = cssRect.width;
      left0 = u.width - (cssRect.top + width0);
      top0 = cssRect.left;
      break;
    default:
      left0 = cssRect.left;
      top0 = cssRect.top;
      width0 = cssRect.width;
      height0 = cssRect.height;
      break;
  }

  const sx = u.width / page.widthPt;
  const sy = u.height / page.heightPt;
  const width = width0 / sx;
  const height = height0 / sy;
  const x = left0 / sx;
  // Y flip: top0 is the rect's TOP edge in CSS; PDF y is the bottom-left.
  const y = page.heightPt - top0 / sy - height;
  return { x, y, width, height };
}

/**
 * Map a pointer-MOVE delta (CSS pixels, in the rotated canvas) to a PDF-space
 * delta in the page's original frame. Used by move/resize drags so they follow
 * the pointer correctly on rotated pages. This is the linear part of
 * {@link clickToPdfPointRotated} (no translation/clamp), so a drag that moves
 * the pointer right on screen moves the edit in the matching PDF direction
 * regardless of the page's /Rotate.
 */
export function cssDeltaToPdfDelta(
  cssDx: number,
  cssDy: number,
  page: { widthPt: number; heightPt: number },
  box: { width: number; height: number },
  delta: RotationDelta,
): { dx: number; dy: number } {
  const u =
    delta === 90 || delta === 270
      ? { width: box.height, height: box.width }
      : { width: box.width, height: box.height };
  const ax = page.widthPt / u.width; // PDF points per CSS px along the X axis
  const ay = page.heightPt / u.height; // PDF points per CSS px along the Y axis
  switch (delta) {
    case 90:
      return { dx: cssDy * ax, dy: cssDx * ay };
    case 180:
      return { dx: -cssDx * ax, dy: cssDy * ay };
    case 270:
      return { dx: -cssDy * ax, dy: -cssDx * ay };
    default:
      // Unrotated: CSS-right = PDF-right, CSS-down = PDF-y-down (Y flipped).
      return { dx: cssDx * ax, dy: -cssDy * ay };
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
